import type { CustomFormAnswerItem, CustomFormField, PlayerRole } from '@/services/communityService';

export const BADMINTON_LEVEL_TEMPLATE = 'level' as const;

export const CANONICAL_BADMINTON_LEVELS = [
  'Level S',
  'Level A',
  'Level B',
  'Level C',
  'Level D',
] as const;

export type CanonicalBadmintonLevel = (typeof CANONICAL_BADMINTON_LEVELS)[number];

/** Form-local role shape used by Create/Edit Mabar community pages. */
export interface LevelFormPlayerRole {
  id: string;
  role: string;
  jumlahPemain: number;
  biayaPerOrang: number;
}

export function isCanonicalBadmintonLevel(value: string): value is CanonicalBadmintonLevel {
  return (CANONICAL_BADMINTON_LEVELS as readonly string[]).includes(value);
}

export function isBadmintonSport(slug?: string | null): boolean {
  return (slug || '').toLowerCase() === 'badminton';
}

export function readParticipantLevel(input: {
  role?: string | null;
  answers?: Array<Pick<CustomFormAnswerItem, 'template_type' | 'value'>> | null;
}): string {
  for (const ans of input.answers || []) {
    if (ans.template_type !== BADMINTON_LEVEL_TEMPLATE) continue;
    const value = String(ans.value ?? '').trim();
    if (isCanonicalBadmintonLevel(value)) return value;
  }
  const role = (input.role || '').trim();
  if (isCanonicalBadmintonLevel(role)) return role;
  return '';
}

export function withUpdatedLevelAnswer(
  answers: CustomFormAnswerItem[] | undefined,
  form: CustomFormField[] | undefined,
  level: string
): CustomFormAnswerItem[] {
  const next = [...(answers || [])];
  const idx = next.findIndex((item) => item.template_type === BADMINTON_LEVEL_TEMPLATE);
  if (idx >= 0) {
    next[idx] = { ...next[idx], value: level, template_type: BADMINTON_LEVEL_TEMPLATE };
    return next;
  }
  const field = form?.find(
    (item) =>
      item.template_type === BADMINTON_LEVEL_TEMPLATE || item.template === BADMINTON_LEVEL_TEMPLATE
  );
  next.push({
    field_id: field?.id,
    question_id: field?.id,
    title: field?.title || 'Level',
    type: 'option',
    template_type: BADMINTON_LEVEL_TEMPLATE,
    value: level,
  });
  return next;
}

export function eventHasLevelTemplate(fields?: CustomFormField[] | null): boolean {
  if (!fields?.length) return false;
  return fields.some(
    (f) => f.template_type === BADMINTON_LEVEL_TEMPLATE || f.template === BADMINTON_LEVEL_TEMPLATE
  );
}

export function buildDefaultLevelPlayerRoles(): PlayerRole[] {
  return CANONICAL_BADMINTON_LEVELS.map((role) => ({
    role,
    slots: 1,
    price: 0,
  }));
}

export function buildDefaultLevelFormPlayerRoles(price = 0): LevelFormPlayerRole[] {
  return CANONICAL_BADMINTON_LEVELS.map((role, index) => ({
    id: `level-role-${index}`,
    role,
    jumlahPemain: 1,
    biayaPerOrang: price,
  }));
}

export function buildLevelCustomFormField(existingId?: string): CustomFormField {
  return {
    id: existingId || `field-level-${Date.now()}`,
    title: 'Level',
    type: 'option',
    required: true,
    options: [...CANONICAL_BADMINTON_LEVELS],
    template_type: BADMINTON_LEVEL_TEMPLATE,
    template: BADMINTON_LEVEL_TEMPLATE,
    read_only_options: true,
  };
}

export function shouldConfirmLevelGenerate(
  rotationFormat: string,
  customForm?: CustomFormField[] | null
): boolean {
  return rotationFormat === 'random_rotation' && eventHasLevelTemplate(customForm);
}
